/**
 * Environment target (client side).
 *
 * Founder 2026-07-19: switch between the test and prod backends from inside
 * the staff console instead of maintaining two deployments pinned to one
 * backend each.
 *
 * HOW THE HEADER GETS SENT — a global fetch wrapper, not per-call-site.
 * The API clients (verificationApi / moderationApi / embedsApi / mailApi) call
 * `fetch` directly from ~30 places. Threading a header through all of them
 * would work exactly until the next call site forgot to, and a call that
 * silently hits the WRONG ENVIRONMENT is the worst possible failure mode here.
 * So we patch `window.fetch` once at boot and stamp every same-origin
 * `/api/v2/*` request. Zero call-site churn, and impossible to forget.
 *
 * SWITCHING RELOADS THE PAGE. TanStack Query caches, in-flight requests and
 * component state all hold data from the previous environment; clearing them
 * piecemeal is a guessing game where a miss means showing prod rows under a
 * "test" label. A reload is one line and cannot be partially right.
 */

export type EnvKey = 'test' | 'prod';

const STORAGE_KEY = 'postopia.q.env';
const ENV_HEADER = 'X-Postopia-Env';

function isEnvKey(v: unknown): v is EnvKey {
  return v === 'test' || v === 'prod';
}

/** The environment this tab is talking to. Defaults to test — the safe side of
 *  a mistake, since prod is where a wrong click is expensive. The server
 *  re-resolves independently and is the authority; this is just what we ask
 *  for and what we label the UI with. */
export function currentEnv(): EnvKey {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isEnvKey(raw)) return raw;
  } catch {
    /* private mode / storage disabled — fall through to the default */
  }
  return 'test';
}

/** Switch environments and reload. See the module note on why we reload. */
export function setEnv(next: EnvKey): void {
  if (next === currentEnv()) return;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* if we cannot persist it, do not pretend we switched */
    return;
  }
  window.location.reload();
}

export type EnvAvailability = {
  available: EnvKey[];
  default: EnvKey;
};

/** Which environments this deployment can actually reach. The switcher hides
 *  anything unconfigured rather than offering a choice that only 503s. */
export async function fetchEnvAvailability(): Promise<EnvAvailability> {
  const res = await fetch('/api/v2/env', { credentials: 'include' });
  if (!res.ok) throw new Error(`env_unavailable_${res.status}`);
  const body = (await res.json()) as Partial<EnvAvailability>;
  const available = Array.isArray(body.available)
    ? body.available.filter(isEnvKey)
    : [];
  return {
    available,
    default: isEnvKey(body.default) ? body.default : 'test',
  };
}

let installed = false;

/**
 * Stamp every same-origin /api/v2/* request with the chosen environment.
 * Idempotent — safe to call more than once (HMR re-runs module init).
 *
 * Scoped to /api/v2/* on purpose: /api/v1/mail talks to the shared mail daemon
 * (MyVigator + Postopia), which has nothing to do with the Postopia worker
 * environment and must not be retargeted.
 */
export function installEnvHeader(): void {
  if (installed) return;
  installed = true;

  const original = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    let path: string;
    try {
      path =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.pathname
            : input.url;
    } catch {
      return original(input as RequestInfo, init);
    }

    // Same-origin v2 API only. Absolute URLs to other hosts are left alone —
    // we must never leak which environment staff are viewing to a third party.
    const isV2 =
      path.startsWith('/api/v2/') ||
      path.startsWith(`${window.location.origin}/api/v2/`);
    if (!isV2) return original(input as RequestInfo, init);

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    headers.set(ENV_HEADER, currentEnv());

    if (input instanceof Request && !init) {
      return original(new Request(input, { headers }));
    }
    return original(input as RequestInfo, { ...init, headers });
  };
}
